import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  BodySmall,
  ParagraphSmallText,
} from '../../../../../../components/Texts';
import { Label } from '../../../../../../components/Inputs/Text';
import {
  PHONE_MEDIA_QUERY,
  useMediaQuery,
} from '../../../../../../common/responsive';
import DigitalIcon from '../../../../../../assets/icons/digital.svg?react';
import HouseIcon from '../../../../../../assets/icons/house.svg?react';
import DateInput from '../../../../../../components/Inputs/Date';
import { useFlag } from '@unleash/proxy-client-react';
import {
  EVENT_GIFT_FLOW_DIGITAL_SELECTED,
  EVENT_GIFT_FLOW_PHYSICAL_SELECTED,
  sendEvent,
} from '../../../../../../common/analytics';
import { useGiftForm } from '../../../../../GiftFormContext';
import { Controller } from 'react-hook-form';
import { useRef } from 'react';
import { hapticsImpactLight } from '../../../../../../common/haptic';
import { getTodayDate, isToday } from '../../../../../../common/date';

const PaymentMethodContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
`;

const PaymentMethodButton = styled.button`
  flex: 1;
  height: 62px;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  font-weight: 600;
  font-size: 16px;
  gap: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  position: relative;
`;

const PaymentMethodTextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const PaymentMethodText = styled(ParagraphSmallText)`
  font-weight: 600;
  line-height: 20px;
  text-align: left;
  color: ${({ theme }) => theme.text.title};
`;

const NewIcon = styled.div`
  background-color: ${({ theme }) => theme.status.error.primary};
  font-size: 10px;
  color: white;
  padding: 2px 6px;
  position: absolute;
  border-radius: 8px;
  right: 8px;
  top: 8px;
`;

const FreeIcon = styled.div`
  background-color: ${({ theme }) => theme.primary.light};
  font-size: 10px;
  color: white;
  padding: 2px 6px;
  position: absolute;
  border-radius: 8px;
  right: 8px;
  top: 8px;
`;

const DeliveryMethods = ({ handleDeliveryMethodChange, delivery }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const theme = useTheme();
  const physicalGiftEnabled = useFlag('physicalGiftEnabled');
  const { showFreeGift, control, setValue, errors, sendOnDate } = useGiftForm();
  const dateRef = useRef(null);

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setValue('sendOnDate', newDate);
    hapticsImpactLight();
  };

  return (
    <>
      {physicalGiftEnabled && (
        <div style={{ width: '100%' }}>
          <Label>{t('giftFlow.delivery.giftCardType')}</Label>
          <PaymentMethodContainer>
            <PaymentMethodButton
              $active={delivery === 'link' || delivery === 'email'}
              onClick={() => {
                handleDeliveryMethodChange('link');
                sendEvent(EVENT_GIFT_FLOW_DIGITAL_SELECTED, {
                  is_free_gift: showFreeGift,
                });
              }}
            >
              {showFreeGift && (
                <FreeIcon>
                  <span>{t('giftFlow.delivery.free')}</span>
                </FreeIcon>
              )}
              {!isMobile && (
                <DigitalIcon
                  width={20}
                  height={20}
                  color={theme.text.regular}
                />
              )}
              <PaymentMethodTextContainer>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {isMobile && (
                    <DigitalIcon
                      width={20}
                      height={20}
                      color={theme.text.regular}
                    />
                  )}
                  <PaymentMethodText>{t('giftFlow.delivery.digital')}</PaymentMethodText>
                </div>
              </PaymentMethodTextContainer>
            </PaymentMethodButton>
            <PaymentMethodButton
              $active={delivery === 'mail'}
              onClick={() => {
                handleDeliveryMethodChange('mail');
                sendEvent(EVENT_GIFT_FLOW_PHYSICAL_SELECTED, {
                  is_free_gift: showFreeGift,
                });
              }}
            >
              <NewIcon>
                <span>{t('giftFlow.delivery.new')}</span>
              </NewIcon>
              {!isMobile && (
                <HouseIcon width={20} height={20} color={theme.text.regular} />
              )}
              <PaymentMethodTextContainer>
                <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {isMobile && (
                    <HouseIcon
                      width={20}
                      height={20}
                      color={theme.text.regular}
                    />
                  )}
                  <PaymentMethodText>{t('giftFlow.delivery.physical')}</PaymentMethodText>
                </div>
              </PaymentMethodTextContainer>
            </PaymentMethodButton>
          </PaymentMethodContainer>
        </div>
      )}
      {['link', 'email'].includes(delivery) && (
        <div style={{ width: '100%' }}>
          <Controller
            name='sendOnDate'
            control={control}
            defaultValue={getTodayDate()}
            render={({ field }) => (
              <DateInput
                ref={(el) => {
                  field.ref(el);
                  dateRef.current = el;
                }}
                label={t('giftFlow.delivery.sendOn')}
                value={field.value || getTodayDate()}
                onChange={(e) => {
                  handleDateChange(e);
                  field.onChange(e);
                }}
                onBlur={field.onBlur}
                name={field.name}
                min={getTodayDate()}
                tooltip={
                  isToday(field.value || getTodayDate())
                    ? t('giftFlow.delivery.tooltipToday')
                    : t('giftFlow.delivery.tooltipFuture')
                }
              />
            )}
          />
        </div>
      )}
    </>
  );
};

export default DeliveryMethods;
